"use client";
import { useState } from "react";
import { GlossaryEntry, GlossaryTableHeaders } from "./ITGlossary";
import GlossaryMobile from "./GlossaryMobile";
import GlossaryDesktop from "./GlossaryDesktop";

export interface GlossaryProps {
  tableHeaders: GlossaryTableHeaders;
  entries: GlossaryEntry[];
}

export const ALPHABET = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
] as const;

export type AlphabetKey = (typeof ALPHABET)[number];

export type Tabs = Record<AlphabetKey, GlossaryEntry[]>;

export const isAlphabetKey = (value: unknown): value is AlphabetKey => {
  return ALPHABET.some((letter) => letter === value);
};

const Glossary = ({ tableHeaders, entries }: GlossaryProps) => {
  const [selectedLetter, setSelectedLetter] = useState<AlphabetKey>("A");

  const tabs: Tabs = {} as Tabs;
  ALPHABET.forEach((letter) => {
    tabs[letter] = [];
  });

  entries.forEach((entry) => {
    const indexFirstLetter = entry.index.slice(0, 1).toUpperCase();
    if (isAlphabetKey(indexFirstLetter)) {
      tabs[indexFirstLetter].push(entry);
    }
  });

  ALPHABET.forEach((letter) => {
    tabs[letter] = tabs[letter].sort((a, b) => {
      const valA = a.index;
      const valB = b.index;

      // undefined values are sorted to the end of array
      if (valA === undefined && valB !== undefined) {
        return 1;
      }
      if (valA !== undefined && valB === undefined) {
        return -1;
      }
      if (valA === undefined || valB === undefined) {
        return 0;
      }

      return valA.localeCompare(valB);
    });
  });

  return (
    <>
      <GlossaryMobile
        selectedLetter={selectedLetter}
        setSelectedLetter={setSelectedLetter}
        tabs={tabs}
      />
      <GlossaryDesktop
        selectedLetter={selectedLetter}
        setSelectedLetter={setSelectedLetter}
        tabs={tabs}
        tableHeaders={tableHeaders}
      />
    </>
  );
};

export default Glossary;
